import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { SIGN_OUT_FAILED_MESSAGE, sessionAlreadyEnded } from "../lib/signOut";
import { markSignup } from "../lib/usageTracker";

const AuthCtx = createContext(null);

// Session lives entirely in the httpOnly `access_token` cookie set by the
// backend. We never persist the JWT to localStorage. On mount we probe
// `/auth/me` — the cookie (if any) authenticates us; a 401 means anonymous.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  // Returns the signed-in USER on success and `false` on failure — still
  // truthy/falsy for every `if (ok)` caller, but carrying the one fact the
  // caller needs and cannot otherwise have yet (#384 item 3): the role.
  // `setUser` below does not help there — the component that called this is
  // mid-handler with the PREVIOUS context value, and it navigates before
  // React re-renders it. Sign-in has to decide where a coach lands, and a
  // coach's home is no longer the same page as a player's.
  // `remember` (#515): the login form's "Keep me signed in on this device"
  // box. Sent as a strict boolean — the API's schema rejects anything else
  // — and default false, so a caller that never passes it (tests, any
  // older call site) posts the same opt-out sign-in as before.
  // #1183: `loginResult` carries the failure reason in its OWN return value
  // (`{ ok, user }` / `{ ok: false, error }`) — a caller that toasts `error`
  // read from `useAuth()` after the `await` is reading the render BEFORE the
  // request, which is the stale closure (normally ""), never what this call
  // just set. `login` is `loginResult` narrowed to its long-standing
  // user-or-false contract for every caller that only needs truthiness.
  const loginResult = async (email, password, remember = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password, remember: remember === true });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(message);
      return { ok: false, error: message };
    }
  };
  const login = async (email, password, remember = false) => {
    const r = await loginResult(email, password, remember);
    return r.ok ? r.user : false;
  };

  // Returns the CREATED USER on success and `false` on failure — the shape
  // `login` above already has, and for the same reason (#714): sign-up can
  // now create a coach, a coach's home is not a player's, and the component
  // that called this is mid-handler with the previous context value when it
  // navigates. Still truthy/falsy for every `if (ok)` caller.
  //
  // #1183: `registerResult` is the same call with the outcome in its own
  // return value (`{ ok, user }` / `{ ok: false, error }`), so the sign-up
  // screens can show the SERVER's reason ("Email already registered") instead
  // of the stale `error` binding from the render before the request.
  // `register` stays the narrowed user-or-false form.
  const registerResult = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      markSignup(); // #1594: before the session is set, so the tracker's first start sees it
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(message);
      return { ok: false, error: message };
    }
  };
  const register = async (payload) => {
    const r = await registerResult(payload);
    return r.ok ? r.user : false;
  };

  // Lets a page that just persisted a change to the user (e.g. Settings'
  // display name save, issue #76) push the server's response straight into
  // context — every consumer (topbar, greeting, Settings itself) re-renders
  // with the new value immediately, no /auth/me re-fetch and no reload.
  const updateUser = (data) => {
    setUser(data);
  };

  // Issue #295: the self-service password change. Returns a result object
  // rather than the boolean the other actions use, because this one has a
  // caller that must show the server's specific reason inline (wrong current
  // password vs. too short vs. same as the old one) on a screen the user
  // cannot leave — a generic "something went wrong" there is a dead end.
  //
  // The response body is the updated user, so pushing it into context is
  // what lifts ProtectedRoute's gate; the server also issued a fresh session
  // cookie (it revoked every session, including this caller's, as part of
  // the change), so no re-login is needed.
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(data);
      return { ok: true, error: "" };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  };

  // Issue #326. Returns `{ ok, error }` — like `changePassword` above, and
  // for a sharper version of the same reason: the caller has to be able to
  // tell a sign-out that happened from one that did not.
  //
  // What this replaced: a `try/catch` that logged the failure with
  // `console.debug` and then ran `setUser(false)` unconditionally. The
  // session lives in an httpOnly cookie that only the server can revoke, so
  // clearing the in-memory user after a FAILED POST does not end the
  // session — it just stops this tab from being able to see it. The device
  // stayed signed in, the screen said otherwise, and `signOutAndGoHome`
  // then reloaded onto the anonymous landing page to make it convincing.
  //
  // So on failure: no `setUser(false)`. Leaving the user in context is the
  // honest state (they ARE still signed in) and it is also the safe one —
  // the app keeps showing whose session this is, and the sign-out control
  // stays available to press again. Only a server-confirmed end clears it.
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // A 401 is the server saying this cookie authenticates nobody, which
      // is the outcome we asked for. Everything else — 5xx, 429, timeout
      // (#336), dropped connection — leaves the session's fate unknown, and
      // an unknown must be reported as a failure, never assumed away.
      if (!sessionAlreadyEnded(e)) {
        // Deliberately NOT the server's `detail`, which the other actions
        // surface. Whatever a 503 says about itself, the only fact this user
        // needs is "you are still signed in on this device" — a proxy's
        // "Service Unavailable" in a toast reads as a problem with the app,
        // not as a warning about the phone in their hand.
        return { ok: false, error: SIGN_OUT_FAILED_MESSAGE };
      }
    }
    setUser(false);
    return { ok: true, error: "" };
  };

  // Issue #1032: the account was just deleted server-side, which ends the
  // session without a logout call. The same reset `logout` performs on
  // success, with no request: the caller then leaves the SPA with a full page
  // load (`DeleteAccountPage`).
  const clearSession = () => {
    setUser(false);
  };

  // Issue #515: "Sign out everywhere" — revokes EVERY session this account
  // holds, on every device, the long-lived "keep me signed in" ones
  // included, and this one. Same contract and same failure discipline as
  // `logout` above, for the same reasons: a 401 means the server already
  // considers this cookie worthless (which for THIS device is the outcome
  // asked for — though it also means the everywhere-revoke did not run
  // under this session, so the caller lands on /login and can sign in and
  // press it again); any other failure leaves every session's fate unknown
  // and must be reported, never assumed away. Only a server-confirmed
  // outcome clears the in-memory user.
  const logoutEverywhere = async () => {
    try {
      await api.post("/auth/sign-out-everywhere");
    } catch (e) {
      if (!sessionAlreadyEnded(e)) {
        return { ok: false, error: SIGN_OUT_FAILED_MESSAGE };
      }
    }
    setUser(false);
    return { ok: true, error: "" };
  };

  // Issue #507: the dual-role active-hat switch. POSTs the new role to
  // `/auth/active-role`, which verifies the caller holds it, revokes and
  // REISSUES the session (a fresh httpOnly cookie lands in the response, so
  // the user stays signed in wearing the new hat), and returns the updated
  // publicUser. Pushing that straight into context re-renders every consumer
  // — the topbar, the sidebar nav, ProtectedRoute's gate — under the new
  // role in one gesture, no /auth/me re-fetch. Returns the new user on
  // success (carrying `role`, so the caller can navigate to that role's
  // home) and `false` on failure, matching `login`'s contract.
  //
  // Only offered to multi-role accounts (AccountMenu renders the control
  // when `roles.length > 1`); a single-role account never calls this, so
  // its behaviour is unchanged.
  const switchActiveRole = async (role) => {
    setError("");
    try {
      const { data } = await api.post("/auth/active-role", { role });
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  // Phase 0 demo role switcher (docs/SPECIFICATION.md §9): sets the demo_role
  // cookie via the fixture API, then re-probes /auth/me for the new persona.
  // No-op against a real backend that doesn't implement /api/demo/role.
  //
  // Issue #874 S1 review (B3) — this is the ONE place that call is made.
  // `exploreAsResult` carries the outcome AND, on failure, the server's own
  // message, in its own return value — never `error` (below), which is
  // context STATE: a caller that reads it via `useAuth()` after `await`ing
  // this is reading whatever render supplied that binding, not necessarily
  // what THIS call just wrote (the exact stale-read shape `Login.jsx`'s own
  // `toast.error(error || …)` has, and that a caller needing the fresh text
  // — `SignInNew.jsx`'s inline demo-failure message — must not repeat).
  // `exploreAs` is `exploreAsResult` narrowed to its own long-standing
  // boolean contract, unchanged for its four callers (`Layout.jsx`,
  // `FrontDoorNew.jsx`, `Landing.jsx`, `Login.jsx`), which never read
  // anything but truthiness. Only the first two (`Landing.jsx`,
  // `Login.jsx`) predate `exploreAsResult`.
  const exploreAsResult = async (role) => {
    setError("");
    try {
      await api.post("/demo/role", { role });
      const { data } = await api.get("/auth/me");
      setUser(data);
      return { ok: true };
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(message);
      return { ok: false, error: message };
    }
  };
  const exploreAs = async (role) => (await exploreAsResult(role)).ok;

  // Issue #510: the magic-link exchange. The callback page reads the token
  // from the emailed link's query string and hands it here; this POSTs it to
  // `/auth/sign-in-link/consume`, which consumes the token single-use and
  // issues a session (a fresh httpOnly cookie lands in the response, so the
  // user is signed in). The response is the publicUser — pushing it into
  // context lifts ProtectedRoute's gate exactly as `login` does, and (for a
  // must_change_password account) carries the flag that routes the SPA to
  // the forced-change screen, because a magic-link sign-in preserves that
  // gate rather than clearing it. Returns the signed-in user on success
  // (carrying `role`, so the caller can land the right home) and `false` on
  // failure — `login`'s contract, so the callback can branch the same way.
  const completeSignInLink = async (token) => {
    setError("");
    try {
      const { data } = await api.post("/auth/sign-in-link/consume", { token });
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  // Issue #561: the passkey exchange — `completeSignInLink`'s shape, for the
  // same reason. Login.jsx runs the browser's WebAuthn ceremony (that half is
  // page lifecycle: conditional UI on mount, aborted on unmount) and hands
  // the finished assertion here; this POSTs it to the verify endpoint, which
  // consumes the challenge, checks the signature, and issues the standard
  // 24h session (never the 30-day remember window — that is the password
  // form's checkbox alone). The response is the same publicUser every door
  // returns — pushing it into context lifts ProtectedRoute's gate, and for
  // a must_change_password account carries the flag that routes the SPA to
  // the forced-change screen (a passkey sign-in preserves that gate, exactly
  // as the magic link does). Returns the signed-in user on success (carrying
  // `role`, so the caller lands the right home) and `false` on failure.
  const completePasskeySignIn = async (assertion) => {
    setError("");
    try {
      const { data } = await api.post("/auth/passkey/authenticate/verify", assertion);
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  // Issue #562: passkey-first SIGNUP's second phase — `completePasskeySignIn`'s
  // shape, for registration. Register.jsx validates the form, fetches the
  // creation options (fields validated server-side with the same schema as
  // password registration), runs the browser ceremony, and hands the account
  // fields plus the finished credential here; this POSTs them to the verify
  // endpoint, which re-validates the fields, checks the attestation, creates
  // the account (role "player", no password door — see the API's notes), and
  // issues the standard 24h session. Pushing the response into context lifts
  // ProtectedRoute's gate exactly as `register` does. Returns the created
  // user on success and `false` on failure, with `error` set for the toast.
  //
  // #1183: `completePasskeySignUpResult` carries the reason in its return
  // value, as `registerResult` does; `completePasskeySignUp` is the narrowed
  // user-or-false form.
  const completePasskeySignUpResult = async (fields, credential) => {
    setError("");
    try {
      const { data } = await api.post("/auth/passkey/register-new/verify", { ...fields, credential });
      markSignup(); // #1594
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(message);
      return { ok: false, error: message };
    }
  };
  const completePasskeySignUp = async (fields, credential) => {
    const r = await completePasskeySignUpResult(fields, credential);
    return r.ok ? r.user : false;
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        error,
        login,
        loginResult,
        register,
        registerResult,
        logout,
        logoutEverywhere,
        clearSession,
        exploreAs,
        exploreAsResult,
        switchActiveRole,
        setError,
        updateUser,
        changePassword,
        completeSignInLink,
        completePasskeySignIn,
        completePasskeySignUp,
        completePasskeySignUpResult,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
